/**
 * CostTrendChart — daily-spend line + area, hand-rolled as inline SVG.
 *
 * Adapted from fidelity/LikenessTrendChart: same no-charting-library posture
 * (the repo intentionally ships no chart dependency), same viewBox/scaling
 * maths. Differences: a single series, a dollar y-axis that IS auto-scaled
 * (spend has no fixed 0..1 ceiling), and a filled area under the line.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CostDailyPoint } from '../../../services/quillAdminService';
import { parseYmdLocal } from './parseYmdLocal';

const W = 720;
const H = 240;
const PAD = { top: 12, right: 16, bottom: 30, left: 52 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

function shortDate(day: string): string {
  const d = parseYmdLocal(day);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function money(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}k`;
  if (n >= 1) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `$${n.toFixed(2)}`;
}

/** Backend numeric aggregates arrive from node-pg as strings — coerce. */
function num(v: number | string | null | undefined): number {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

const CostTrendChart: React.FC<{ points: CostDailyPoint[] }> = ({ points }) => {
  const theme = useTheme();
  const color = theme.palette.primary.main;

  // The backend keys each point on `day` (not `date`) and cost on `est_cost_usd`.
  // Parse `day` as LOCAL-tz midnight (parseYmdLocal) so US viewers don't see the
  // previous calendar day (D5).
  const valid = (points ?? []).filter((p) => p && !Number.isNaN(parseYmdLocal(p.day).getTime()));
  if (valid.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No spend recorded in this range yet — nothing to trend.
      </Typography>
    );
  }

  const times = valid.map((p) => parseYmdLocal(p.day).getTime());
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const span = maxT - minT;
  // The `day` strings at the range endpoints, for the axis labels (kept as
  // strings so shortDate re-parses them in local tz — no UTC round-trip).
  const minDay = valid[times.indexOf(minT)].day;
  const maxDay = valid[times.indexOf(maxT)].day;

  const maxCost = Math.max(...valid.map((p) => num(p.est_cost_usd)), 0);
  // A little headroom so the peak doesn't sit on the top gridline.
  const yMax = maxCost <= 0 ? 1 : maxCost * 1.1;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax);

  const x = (day: string): number => {
    const t = parseYmdLocal(day).getTime();
    if (!span) return PAD.left + PLOT_W / 2;
    return PAD.left + ((t - minT) / span) * PLOT_W;
  };
  const y = (v: number): number => PAD.top + (1 - Math.max(0, Math.min(1, v / yMax))) * PLOT_H;

  const linePoints = valid.map((p) => `${x(p.day).toFixed(1)},${y(num(p.est_cost_usd)).toFixed(1)}`);
  const areaPath =
    valid.length > 1
      ? `M ${x(valid[0].day).toFixed(1)},${y(0).toFixed(1)} ` +
        valid.map((p) => `L ${x(p.day).toFixed(1)},${y(num(p.est_cost_usd)).toFixed(1)}`).join(' ') +
        ` L ${x(valid[valid.length - 1].day).toFixed(1)},${y(0).toFixed(1)} Z`
      : '';

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Daily spend trend"
        style={{ width: '100%', height: 'auto', display: 'block', minWidth: 320 }}
      >
        <title>Estimated AI spend per day</title>

        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} x2={PAD.left + PLOT_W} y1={y(t)} y2={y(t)} stroke={theme.palette.divider} strokeWidth={1} />
            <text x={PAD.left - 6} y={y(t) + 3.5} textAnchor="end" fontSize={10} fill={theme.palette.text.secondary}>
              {money(t)}
            </text>
          </g>
        ))}

        <text x={PAD.left} y={H - 10} fontSize={10} fill={theme.palette.text.secondary}>
          {shortDate(minDay)}
        </text>
        {span > 0 && (
          <text x={PAD.left + PLOT_W} y={H - 10} fontSize={10} textAnchor="end" fill={theme.palette.text.secondary}>
            {shortDate(maxDay)}
          </text>
        )}

        {areaPath && <path d={areaPath} fill={color} fillOpacity={0.12} stroke="none" />}
        {valid.length > 1 && (
          <polyline
            points={linePoints.join(' ')}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}
        {valid.map((p) => (
          <circle key={p.day} cx={x(p.day)} cy={y(num(p.est_cost_usd))} r={3} fill={color}>
            <title>{`${shortDate(p.day)} · ${money(num(p.est_cost_usd))}${p.calls != null ? ` · ${num(p.calls)} calls` : ''}`}</title>
          </circle>
        ))}
      </svg>
    </Box>
  );
};

export default CostTrendChart;
